/**
 * Shared drill-down side drawer.
 *
 * Any table/chart cell across the app (outside Executive BI, Underpayment,
 * and Payroll) can call `openDrilldown(...)` via context to open this
 * drawer, which fetches the underlying record list from a backend
 * drill-down endpoint and lets the user click a row to jump to that
 * record's canonical page (Pipeline, Inquiries, etc.).
 *
 * Financial fields are NEVER surfaced in the drill-in list — the drawer
 * is used in cross-role reviews (Director meetings included), so anything
 * dollar-denominated stays out of the API response.
 *
 * Usage:
 *   import { useDrilldown } from './DrillDownDrawer';
 *   const { openDrilldown } = useDrilldown();
 *   openDrilldown({
 *     title: 'Evals scheduled — March 2026',
 *     subtitle: 'Katy · Pediatrician',
 *     endpoint: '/drilldown/referral-cohort',
 *     params: { month: '2026-03', metric: 'evals_scheduled', scope: 'Katy', source: 'Pediatrician' },
 *     rowNav: (row) => `/pipeline?open=${row.client_id}`,
 *   });
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Download, AlertCircle, Search } from 'lucide-react';
import api from '../lib/api';
import { fmtDate } from '../lib/date';

const C = {
  ink: '#2D2833', soft: '#685B72', border: '#E9E3DA',
  paper: '#FBF8F4', gold: '#B08A3A', teal: '#3F7D6E', rose: '#8A3A3A',
};

const DrilldownCtx = createContext(null);
export const useDrilldown = () => useContext(DrilldownCtx) || { openDrilldown: () => {} };

const DEFAULT_COLUMNS = [
  { key: 'client_name',        label: 'Client',           format: (v) => v || '—' },
  { key: 'clinic',             label: 'Clinic',           format: (v) => v || '—' },
  { key: 'referral_date',      label: 'Referral',         format: (v) => (v ? fmtDate(v) : '—') },
  { key: 'source_bucket',      label: 'Source',           format: (v) => v || '—' },
  { key: 'eval_date',          label: 'Eval date',        format: (v) => (v ? fmtDate(v) : '—') },
  { key: 'eval_completed',     label: 'Eval done',        format: (v) => v || '—' },
  { key: 'pre_auth_status',    label: 'Pre-auth',         format: (v) => v || '—' },
  { key: 'therapy_start_date', label: 'Therapy start',    format: (v) => (v ? fmtDate(v) : '—') },
  { key: 'days_to_start',      label: 'Days to start',    format: (v) => (v == null ? '—' : `${v}d`) , align: 'right' },
  { key: 'stage',              label: 'Stage',            format: (v) => v || '—' },
];

function DrilldownDrawer({ state, onClose }) {
  const { open, title, subtitle, endpoint, params, columns, rowNav } = state || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !endpoint) return;
    let cancelled = false;
    setLoading(true); setErr(null); setData(null); setQ('');
    api.get(endpoint, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load records'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, endpoint, JSON.stringify(params || {})]);   // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = data?.clients || data?.rows || [];
  const cols = columns && columns.length ? columns : DEFAULT_COLUMNS;

  // Free-text filter — matches any string value in the row (name, clinic,
  // source, status, dates, evaluator, stage, …). Case-insensitive.
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allRows;
    return allRows.filter((r) => {
      for (const v of Object.values(r || {})) {
        if (v == null) continue;
        if (String(v).toLowerCase().includes(needle)) return true;
      }
      return false;
    });
  }, [allRows, q]);

  const doDownload = () => {
    if (!rows.length) return;
    const header = cols.map((c) => c.label).join(',');
    const escape = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = rows.map((r) => cols.map((c) => escape(c.format ? c.format(r[c.key], r) : r[c.key])).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(title || 'drilldown').replace(/[^\w-]+/g, '_')}${q ? '_filtered' : ''}.csv`;
    a.click();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[70]"
        onClick={onClose}
        data-testid="drilldown-backdrop"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[720px] max-w-full bg-white z-[71] shadow-2xl flex flex-col"
        style={{ borderLeft: `1px solid ${C.border}` }}
        role="dialog"
        aria-label={title}
        aria-describedby="drilldown-subtitle-id"
        data-testid="drilldown-drawer"
      >
        <header className="px-5 py-4 border-b flex items-start gap-3" style={{ borderColor: C.border, background: C.paper }}>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.gold }}>Drill-down · read only · no financial data</div>
            <h3 className="font-serif text-lg mt-0.5 leading-snug" style={{ color: C.ink }} data-testid="drilldown-title">{title || 'Records'}</h3>
            {subtitle && <div id="drilldown-subtitle-id" className="text-[11px] mt-1" style={{ color: C.soft }} data-testid="drilldown-subtitle">{subtitle}</div>}
            {!subtitle && <div id="drilldown-subtitle-id" className="sr-only">Drill-down list of records backing the clicked data point.</div>}
          </div>
          <button
            type="button"
            onClick={doDownload}
            disabled={!rows.length}
            title="Download list as CSV"
            className="rounded-full px-3 h-9 inline-flex items-center gap-1.5 text-[12px] font-medium border transition disabled:opacity-40"
            style={{ borderColor: C.border, color: C.ink }}
            data-testid="drilldown-csv"
          >
            <Download size={13} /> CSV
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full w-9 h-9 inline-flex items-center justify-center border transition hover:bg-white"
            style={{ borderColor: C.border, color: C.ink }}
            aria-label="Close drill-down"
            data-testid="drilldown-close"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-8 text-center text-[13px]" style={{ color: C.soft }} data-testid="drilldown-loading">Loading records…</div>
          )}
          {err && (
            <div className="p-6 flex items-start gap-2 text-[13px]" style={{ color: C.rose }} data-testid="drilldown-error">
              <AlertCircle size={16} className="mt-0.5" /> <div>{err}</div>
            </div>
          )}
          {!loading && !err && allRows.length === 0 && (
            <div className="p-10 text-center" data-testid="drilldown-empty">
              <div className="font-serif text-lg" style={{ color: C.ink }}>No records match this data point</div>
              <div className="text-[12px] mt-1" style={{ color: C.soft }}>Nothing rolled up into this cell — try a different month or clear the source filter.</div>
            </div>
          )}
          {!loading && !err && allRows.length > 0 && (
            <>
              <div className="px-5 pt-4 pb-2 flex flex-wrap items-center gap-3 sticky top-0 z-10 bg-white border-b" style={{ borderColor: C.border }}>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.soft }} />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter by name, clinic, source, stage…"
                    className="w-full h-9 pl-8 pr-8 rounded-full border text-[12px] focus:outline-none focus:ring-2 focus:ring-[#B08A3A]/40"
                    style={{ borderColor: C.border, color: C.ink }}
                    data-testid="drilldown-filter"
                  />
                  {q && (
                    <button
                      type="button"
                      onClick={() => setQ('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full inline-flex items-center justify-center hover:bg-[#FBF8F4]"
                      aria-label="Clear filter"
                      data-testid="drilldown-filter-clear"
                    >
                      <X size={11} style={{ color: C.soft }} />
                    </button>
                  )}
                </div>
                <div className="text-[11px] whitespace-nowrap" style={{ color: C.soft }} data-testid="drilldown-count">
                  {q
                    ? <>{rows.length} of {allRows.length} record{allRows.length !== 1 ? 's' : ''}</>
                    : <>{allRows.length} record{allRows.length !== 1 ? 's' : ''}</>
                  }
                  {rowNav && <> · click a row to open in the source page</>}
                </div>
              </div>
              {rows.length === 0 && (
                <div className="p-10 text-center" data-testid="drilldown-filter-empty">
                  <div className="font-serif text-lg" style={{ color: C.ink }}>No matches for "{q}"</div>
                  <button
                    type="button"
                    onClick={() => setQ('')}
                    className="mt-2 text-[12px] underline"
                    style={{ color: C.gold }}
                    data-testid="drilldown-filter-reset"
                  >
                    Clear filter
                  </button>
                </div>
              )}
              {rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-widest" style={{ background: C.paper, color: C.soft }}>
                      {cols.map((c) => (
                        <th key={c.key} className={`py-2 px-3 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const link = rowNav ? rowNav(r) : null;
                      return (
                        <tr
                          key={`${r.client_id || r.id || i}`}
                          className={`border-t ${link ? 'cursor-pointer hover:bg-[#FBF8F4]' : ''} transition`}
                          style={{ borderColor: C.border }}
                          onClick={() => { if (link) { onClose(); navigate(link); } }}
                          data-testid={`drilldown-row-${i}`}
                        >
                          {cols.map((c) => (
                            <td key={c.key} className={`py-2 px-3 tabular-nums ${c.align === 'right' ? 'text-right' : ''}`} style={{ color: C.ink }}>
                              {c.format ? c.format(r[c.key], r) : (r[c.key] ?? '—')}
                            </td>
                          ))}
                          {link && (
                            <td className="py-2 pr-3 text-right" style={{ color: C.soft }}>
                              <ExternalLink size={12} />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export function DrilldownProvider({ children }) {
  const [state, setState] = useState({ open: false });
  const openDrilldown = useCallback((cfg) => setState({ open: true, ...cfg }), []);
  const closeDrilldown = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const value = useMemo(() => ({ openDrilldown, closeDrilldown }), [openDrilldown, closeDrilldown]);
  return (
    <DrilldownCtx.Provider value={value}>
      {children}
      <DrilldownDrawer state={state} onClose={closeDrilldown} />
    </DrilldownCtx.Provider>
  );
}

export default DrilldownDrawer;
